import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import Hint from 'components/styleguide/hint/Hint';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';
import SelectionButton, { SelectionButtonContext, SelectionButtonState } from './SelectionButton';

const styles = require('./SelectionButtonGroup.module.scss');

interface Option {
  label: string;
  value: string;
}

export interface SelectionButtonGroupProps {
  autoSaveUrl?: string;
  context?: SelectionButtonContext;
  // string for simple/radio (single-select); string[] for checkmark (multi).
  defaultSelected?: string | string[];
  fullWidth?: boolean;
  hint?: string;
  // Form name for the hidden inputs / radio inputs. Use a Rails-style
  // `name[]` for checkmark to submit as an array.
  inputName?: string;
  label?: string;
  onChange?: (selected: string | string[]) => void;
  options: Option[];
  state: SelectionButtonState;
}

const toArray = (value?: string | string[]): string[] => {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

const SelectionButtonGroup: React.FC<SelectionButtonGroupProps> = ({
  autoSaveUrl,
  context = 'talent',
  defaultSelected,
  fullWidth,
  hint,
  inputName,
  label,
  onChange,
  options,
  state,
}) => {
  const { save } = useAutoSave({ url: autoSaveUrl || '', name: inputName || '', debounceMs: 0 });
  const isMulti = state === 'checkmark';
  const optionValues = useMemo(() => options.map(o => o.value), [options]);
  const [selected, setSelected] = useState<string[]>(
    () => toArray(defaultSelected).filter(v => optionValues.includes(v)),
  );

  const emit = useCallback((next: string[]) => {
    setSelected(next);
    const payload: string | string[] = isMulti ? next : (next[0] ?? '');
    onChange?.(payload);
    if (autoSaveUrl) save(payload);
  }, [isMulti, onChange, autoSaveUrl, save]);

  const handleChange = useCallback((value: string, isSelected: boolean) => {
    if (isMulti) {
      const next = isSelected
        ? [...selected, value]
        : selected.filter(v => v !== value);
      // Checkmark: at least one must remain selected.
      if (next.length === 0) return;
      emit(next);
    } else {
      // Simple/radio: single-select. Ignore "deselect" clicks; switch only
      // when a different option is picked.
      if (!isSelected) return;
      emit([value]);
    }
  }, [isMulti, selected, emit]);

  const role = state === 'radio' ? 'radiogroup' : 'group';
  const isVertical = fullWidth || state === 'radio';

  return (
    <div className={styles.wrapper} role={role} aria-label={label || undefined}>
      {label && <p className={styles.title}>{label}</p>}
      <div className={classNames(styles.group, { [styles.vertical]: isVertical })}>
        {options.map(option => (
          <SelectionButton
            key={option.value}
            context={context}
            fullWidth={fullWidth}
            label={option.label}
            name={inputName}
            value={option.value}
            state={state}
            selected={selected.includes(option.value)}
            onChange={(isSelected) => handleChange(option.value, isSelected)}
          />
        ))}
      </div>
      {hint && <Hint message={hint} />}
    </div>
  );
};

export default SelectionButtonGroup;
